import { icons } from '../knowledge-base-common/components/tag-icon';
import { Languages } from '../knowledge-base-common/knowledge-base-common.type';
import {
    createArticleApi,
    createCategoryApi,
    createSectionApi,
    createTagApi,
    getById,
    updateArticleApi,
} from './actions';
import { languages } from './admin-knowledge-base.constants';
import {
    Article,
    Category,
    CreateDTO,
    Section,
    Status,
    Statuses,
    Tag,
} from './admin-knowledge-base.type';
import {getRandomSecure} from "mixins/helpers";

const addLangPrefix = (value: string, lang: Languages) =>
    value + (lang === Languages.RU ? '' : ` (${lang})`);

const getRandom = <T>(items: T[]) => items[Math.floor(getRandomSecure() * items.length)];

const statuses: Status[] = [
    { current: Statuses.DRAFT },
    { current: Statuses.PUBLISH },
    { current: Statuses.DRAFT, planned: Statuses.ARCHIVE },
    { current: Statuses.DRAFT, planned: Statuses.PUBLISH },
];

const sectionNames = [
    {
        ru: { title: 'Налоговый раздел' },
        kk: { title: 'Салық бөлімі' },
        en: { title: 'Tax section' },
    },
    {
        ru: { title: 'Таможенный раздел' },
        kk: { title: 'Кедендік бөлім' },
        en: { title: 'Customs section' },
    },
];

const tagNames = [
    {
        ru: { title: 'Вопросы-ответы' },
        kk: { title: 'Сұрақ-жауаптар' },
        en: { title: 'Questions and answers' },
    },
    { ru: { title: 'Видеоролики' }, kk: { title: 'Бейнероликтер' }, en: { title: 'Videos' } },
    { ru: { title: 'Для СМИ' }, kk: { title: 'БАҚ үшін' }, en: { title: 'For the media' } },
    { ru: { title: 'Инструкции' }, kk: { title: 'Нұсқаулық' }, en: { title: 'Instructions' } },
    { ru: { title: 'Комментарии' }, kk: { title: 'Пікірлер орны' }, en: { title: 'Comments' } },
    { ru: { title: 'Правила' }, kk: { title: 'Ережесі' }, en: { title: 'Rules' } },
    { ru: { title: 'Приказы' }, kk: { title: 'Бұйрықтар' }, en: { title: 'Orders' } },
    { ru: { title: 'Регламенты' }, kk: { title: 'Регламенттер' }, en: { title: 'Regulations' } },
    { ru: { title: 'Статьи' }, kk: { title: 'Мақалалар' }, en: { title: 'Articles' } },
    { ru: { title: 'Стандарты' }, kk: { title: 'Стандарттар' }, en: { title: 'Standards' } },
    {
        ru: { title: 'Шаблоны документов' },
        kk: { title: 'Құжат үлгілері' },
        en: { title: 'Document templates' },
    },
    {
        ru: { title: 'Часто задаваемые вопросы' },
        kk: { title: 'Жиі қойылатын сұрақтар' },
        en: { title: 'FAQ' },
    },
];

const taxSectionCategories = [
    { ru: { title: 'Акцизы' }, kk: { title: 'Акциздер' }, en: { title: 'Excise taxes' } },
    { ru: { title: 'Земельный налог' }, kk: { title: 'Жер салығы' }, en: { title: 'Land tax' } },
    {
        ru: { title: 'Индивидуальный подоходный налог (ИПН)' },
        kk: { title: 'Жеке табыс салығы (ЖТС)' },
        en: { title: 'Individual income tax (IIT)' },
    },
    {
        ru: { title: 'Корпоративный подоходный налог (КПН)' },
        kk: { title: 'Корпоративтік табыс салығы (КТС)' },
        en: { title: 'Corporate income tax (CIT)' },
    },
    {
        ru: { title: 'Налог на добавленную стоимость (НДС)' },
        kk: { title: 'Қосылған құн салығы (ҚҚС)' },
        en: { title: 'Value added tax (VAT)' },
    },
    {
        ru: { title: 'Налог на игорный бизнес' },
        kk: { title: 'Ойын бизнесі салығы' },
        en: { title: 'Gambling tax' },
    },
    {
        ru: { title: 'Налог на имущество' },
        kk: { title: 'Мүлік салығы' },
        en: { title: 'Property tax' },
    },
    {
        ru: { title: 'Налог на транспортные средства' },
        kk: { title: 'Көлік құралдарына салынатын салық' },
        en: { title: 'Vehicle tax' },
    },
    {
        ru: { title: 'Налоговый контроль' },
        kk: { title: 'Салықтық бақылау' },
        en: { title: 'Tax control' },
    },
    {
        ru: { title: 'Налоговое обязательство' },
        kk: { title: 'Салық міндеттемесі' },
        en: { title: 'Tax liability' },
    },
    {
        ru: { title: 'Налогообложение доходов' },
        kk: { title: 'Табыстарға салық салу' },
        en: { title: 'Taxation of income' },
    },
    {
        ru: { title: 'Налогообложение на территориях СЭЗ' },
        kk: { title: 'АЭА аумақтарында салық салу' },
        en: { title: 'Taxation in SEZ territories' },
    },
    {
        ru: { title: 'Налогообложение недропользователей' },
        kk: { title: 'Жер қойнауын пайдаланушыларға салық салу' },
        en: { title: 'Taxation of subsoil users' },
    },
    {
        ru: { title: 'Налогообложение нерезидентов' },
        kk: { title: 'Резидент еместерге салық салу' },
        en: { title: 'Taxation of non-residents' },
    },
    {
        ru: {
            title: 'Налогообложение организаций, реализующих инвестиционные приоритетные проекты',
        },
        kk: { title: 'Инвестициялық басым жобаларды іске асыратын ұйымдарға салық салу' },
        en: { title: 'Taxation of organizations implementing priority investment projects' },
    },
    {
        ru: { title: 'Обжалование (в т.ч. действий ОГД)' },
        kk: { title: 'Шағым жасау (соның ішінде, іс-қимыл МКО)' },
        en: { title: 'Appeal (including actions of the SRA)' },
    },
    // {
    //     ru: { title: 'Общие положения' },
    //     kk: { title: 'Жалпы ережелер' },
    //     en: { title: 'General provisions' },
    // },
    {
        ru: { title: 'Платежи в бюджет' },
        kk: { title: 'Бюджетке төленетін төлемдер' },
        en: { title: 'Payments to the budget' },
    },
    {
        ru: { title: 'Резиденты, нерезиденты' },
        kk: { title: 'Резиденттер, резидент еместер' },
        en: { title: 'Residents, non-residents' },
    },
    {
        ru: { title: 'Рентный налог на экспорт' },
        kk: { title: 'Экспортқа рента салығы' },
        en: { title: 'Rental tax on exports' },
    },
    {
        ru: { title: 'Социальный налог' },
        kk: { title: 'Әлеуметтік салық' },
        en: { title: 'Social tax' },
    },
    {
        ru: { title: 'Специальные налоговые режимы (СНР)' },
        kk: { title: 'Арнаулы салық режимдері (АСР)' },
        en: { title: 'Special tax regimes (STR)' },
    },
    {
        ru: { title: 'Фиксированный налог' },
        kk: { title: 'Тіркелген салық' },
        en: { title: 'Flat tax' },
    },
];

const customsSectionCategories = [
    {
        ru: { title: 'Антидемпинговые пошлины' },
        kk: { title: 'Демпингке қарсы баждар' },
        en: { title: 'Anti-dumping duties' },
    },
    {
        ru: { title: 'Компенсационные пошлины' },
        kk: { title: 'Өтемақы баждары' },
        en: { title: 'Countervailing duties' },
    },
    {
        ru: { title: 'Общие положения' },
        kk: { title: 'Жалпы ережелер' },
        en: { title: 'General provisions' },
    },
    {
        ru: { title: 'Специальные пошлины' },
        kk: { title: 'Арнайы баждар' },
        en: { title: 'Special duties' },
    },
    { ru: { title: 'Таможенное дело' }, kk: { title: 'Кеден ісі' }, en: { title: 'Customs' } },
    {
        ru: { title: 'Таможенный контроль' },
        kk: { title: 'Кедендік бақылау' },
        en: { title: 'Customs control' },
    },
    {
        ru: { title: 'Таможенные операции и лица, их совершающие' },
        kk: { title: 'Кедендік операциялар және оларды жасайтын тұлғалар' },
        en: { title: 'Customs operations and persons performing them' },
    },
    {
        ru: { title: 'Таможенное перемещение через ЕЭС' },
        kk: { title: 'БЭЖ арқылы кедендік өткізу' },
        en: { title: 'Customs movement through the EEU' },
    },
    {
        ru: { title: 'Таможенные платежи, налоги' },
        kk: { title: 'Кедендік төлемдер, салықтар' },
        en: { title: 'Customs payments, taxes' },
    },
];

// const categoryNames = [
//     'Индивидуальный подоходный налог (ИПН)',
//     'Корпоративный подоходный налог (КПН)',
//     'Налог на игорный бизнес',
//     'Налог на имущество',
//     'Налог на транспортные средства',
//     // 'Налоговый контроль',
//     // 'Налоговое обязательство',
//     // 'Налогообложение доходов',
//     // 'Налогообложение на территориях СЭЗ',
//     // 'Налогообложение недропользователей',
//     // 'Налогообложение нерезидентов',
// ];

const getLangs = (fields: any): Record<Languages, any> =>
    languages.reduce((acc, lang) => {
        acc[lang] = Object.fromEntries(
            Object.entries(fields).map(([key, value]: [string, string]) => [
                key,
                key === 'content'
                    ? `<p>${addLangPrefix(value, lang)}</p>`
                    : addLangPrefix(value, lang),
            ])
        );
        return acc;
    }, {} as Record<Languages, any>);

export const seed = async () => {
    const tagDTOs = tagNames.map(
        (item): CreateDTO<Tag> => ({
            ...item,
            icon: getRandom(icons) as string,
            status: getRandom(statuses),
        })
    );

    const tags = await Promise.all(tagDTOs.map(createTagApi));

    const sectionDTOs = sectionNames.map(
        (item): CreateDTO<Section> => ({
            ...item,
            status: getRandom(statuses),
        })
    );

    const [taxSection, customsSection] = await Promise.all(sectionDTOs.map(createSectionApi));

    const taxCategoryDTOs = taxSectionCategories.map(
        (item): CreateDTO<Category> => ({
            ...item,
            status: getRandom(statuses),
        })
    );

    const taxCategories = await Promise.all(
        taxCategoryDTOs.map((dto) => createCategoryApi({ ...dto, parent: taxSection.path }))
    );

    const customsCategoryDTOs = customsSectionCategories.map(
        (item): CreateDTO<Category> => ({
            ...item,
            status: getRandom(statuses),
        })
    );

    const customsCategories = await Promise.all(
        customsCategoryDTOs.map((dto) => createCategoryApi({ ...dto, parent: customsSection.path }))
    );

    const categories = [...taxCategories, ...customsCategories];

    const seedArticles = [
        {
            status: {
                current: Statuses.PUBLISH,
            },
            ru: {
                title: 'Топ сервисы',
                content: `<ol>
            <li><a href="https://kgd.isna.kz/ru/pages/info-services/list-persons-on-termination-rehabilitation">Список индивидуальных предпринимателей и юридических лиц, в отношении которых вступило в законную силу определение суда о прекращении реабилитационной процедуры</a>&nbsp;[Цель: Дефолт]</li>
            <li><a href="https://kgd.isna.kz/ru/pages/info-services/online-booking-queue">Онлайн бронирование очереди</a>&nbsp; &nbsp;[Цель:Фрейм]</li>
            <li><a href="https://kgd.isna.kz/ru/pages/info-services/false-deals" onclick="window.open(this.href, '', 'resizable=no,status=no,location=no,toolbar=no,menubar=no,fullscreen=no,scrollbars=no,dependent=no,width=200,height=100'); return false;">Поиск НП, сделки (операции) с которыми совершены без фактического выполнения работ, оказания услуг, отгрузки товаров</a>&nbsp; [Цель: Всплывающее окно]</li>
            <li><a href="https://kgd.isna.kz/ru/pages/info-services/reestr-samoreguliruemyh-organizacij-v-sfere-reabilitacii-i-bankrotstva" target="_blank">Реестр саморегулируемых организаций в сфере реабилитации и банкротства</a>&nbsp; [Цель: Новое окно]</li>
            <li><a href="https://kgd.isna.kz/ru/pages/info-services/returned-notifications" target="_parent">Поиск возвращенных уведомлений</a>&nbsp;[Цель: Родительское]</li>
        </ol>
        
        <p style="text-align:center"><iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen frameborder="0" height="315" src="https://www.youtube.com/embed/oCojy6rMFio?si=f_BIDiW9M434SbAT" width="560"></iframe></p>
        
        <p>&nbsp;</p>
        `,
            },
            kk: {
                title: 'Топ сервистер',
                content: `<ul>
            <li><a href="https://kgd.isna.kz/kk/pages/info-services/list-persons-on-termination-rehabilitation">Оңалту рәсімін тоқтату туралы сот ұйғарымы оларға қатысты заңды күшіне енген дара кәсіпкерлер мен заңды тұлғалардың тізімі</a></li>
            <li><a href="https://kgd.isna.kz/kk/pages/info-services/online-booking-queue">Кезекті онлайн брондау</a></li>
            <li><a href="https://kgd.isna.kz/kk/pages/info-services/false-deals">Жұмыстарды іс жүзінде орындамай, қызметтер көрсетпей, тауарларды тиеп жөнелтпей мәмілелер (операциялар) жасалған СТ іздеу</a></li>
            <li><a href="https://kgd.isna.kz/kk/pages/info-services/reestr-samoreguliruemyh-organizacij-v-sfere-reabilitacii-i-bankrotstva">Оңалту және банкроттық саласындағы өзін-өзі реттейтін ұйымдардың тізілімі</a></li>
            <li><a href="https://kgd.isna.kz/kk/pages/info-services/returned-notifications">Қайтарылған хабарламаларды іздеу</a></li>
        </ul>
        
        <p style="text-align:center"><img alt="" src="/site/ru/draft/isna-portal-admin/_/image/e0f80940-3601-467c-8024-2ac0d2d1a186:7456f0fe98fe95fe2f82d014a204fedb87afd44c/width-768/download-734795662478235435.jpeg" style="height:499px; width:800px" srcset=""></p>
        `,
            },
            en: {
                title: 'Top services',
                content: `<ol>
            <li><a href="https://kgd.isna.kz/en/pages/info-services/reestr-samoreguliruemyh-organizacij-v-sfere-reabilitacii-i-bankrotstva">Register of self-regulatory organizations in the field of rehabilitation and bankruptcy</a></li>
            <li><a href="https://kgd.isna.kz/en/pages/info-services/returned-notifications">Search returned notification</a></li>
            <li><a href="https://kgd.isna.kz/en/pages/info-services/online-booking-queue">Online queue reservation</a></li>
            <li><a href="https://kgd.isna.kz/en/pages/info-services/list-persons-on-termination-rehabilitation">The list of individual entrepreneurs and legal entities in respect of which the court ruling on the termination of the rehabilitation procedure has entered into force</a></li>
        </ol>
        
        <p style="text-align:center"><img alt="" src="image://18f47060-4141-4314-9f31-2389b9bd89a6" style="height:175px; width:650px" /></p>
        `,
            },
        },
        {
            status: {
                current: Statuses.PUBLISH,
            },
            ru: {
                title: 'Информационные системы Комитета государственных доходов',
                content: `<p style="text-align:center"><strong>ИНФОРМАЦИОННЫЕ СИСТЕМЫ КОМИТЕТА ГОСУДАРСТВЕННЫХ ДОХОДОВ</strong></p>
      
      <p style="text-align:center"><strong>ИС КГД</strong></p>
      
      <p style="text-align:center"><strong><img alt="" src="image://933b7afd-c54b-4e01-96c2-27568cff4f9d" style="float:left; height:150px; width:150px" /></strong></p>
      
      <p>Информационные системы Комитета государственных доходов (далее &ndash; Система, ИС КГД) предназначены для комплексной автоматизации (бизнес-процессов) Комитета государственных доходов МФ РК и включают в себя автоматизацию предоставления КГД МФ РК электронных услуг гражданам и бизнесу РК.</p>
      
      <p>Реализация проекта ИС КГД &laquo;Развитие информационных систем в целях Реформирования Налогового и Таможенного Администрирования&raquo; предполагает модернизацию существующих и информационных систем КГД МФ РК и создание новых.</p>
      
      <p>Масштаб Проекта главным образом определяется общим числом налогоплательщиков, задействованных в системе налогообложения, а также числом пользователей информационных систем КГД МФ РК, осуществляющих деятельность в области налогового администрирования. Важным является также число уровней иерархии организационной структуры КГД, определяющих, в том числе, архитектуру проектируемой системы.</p>
      
      <p style="text-align:center"><strong>Цель</strong></p>
      
      <p>Проект предназначен для автоматизации административных процедур по исполнению КГД МФ РК и направлена на бесперебойную работу и развитие существующих и новых информационных систем КГД МФ РК.</p>
      
      <p style="text-align:center"><strong>Задачи</strong></p>
      
      <ol>
          <li>Обеспечить достоверность данных ИС КГД путем интегрирования с компонентами &laquo;электронного правительства&raquo;</li>
          <li>Гарантировать качество и оперативность предоставления информационных, интерактивных, транзакционных услуг для населения, бизнеса и государственных органов</li>
          <li>Снизить издержки на сопровождение и администрирование всей информационной структуры</li>
          <li>Создать гибкие информационные системы &ndash; с высокой степенью адаптации к изменениям законодательства РК, не требующую значительных затрат.</li>
          <li>Повысить качество управленческих решений и эффективность контроля в области управления бюджетными ресурсами и государственными финансами;</li>
          <li>Повысить эффективность управления и сократить непроизводительные затраты;</li>
          <li>Повысить эффективность предоставления государственных услуг для населения и бизнес-сообщества</li>
      </ol>
      
      <p style="text-align:center"><strong>ПРЕИМУЩЕСТВА ДЛЯ ГОСУДАРСТВА</strong></p>
      
      <ul>
          <li>Оперативность и повышение эффективности взаимодействия государственных ведомств<br />
          <em>Все сопутствующие процессы по управлению бюджетом переведены в электронный формат, централизованы, что сделало межведомственное взаимодействие более эффективным.</em></li>
          <li>Качество и достоверность</li>
          <li>Автоматическая консолидация информации позволяет исключить &laquo;человеческий фактор&raquo;;</li>
          <li>Экономия<br />
          <em>Снижение издержек и трудовых ресурсов на сбор и консолидацию большого количества отчетов.</em></li>
      </ul>
      
      <p style="text-align:center"><strong>ПРЕИМУЩЕСТВА ДЛЯ НАСЕЛЕНИЯ И БИЗНЕСА</strong></p>
      
      <ul>
          <li>Снижение административных барьеров и повышение оперативности и качества оказания услуг КГД МФ РК;</li>
          <li>Электронная аккредитация аудиторов, получение лицензий в рамках компетенции КГД МФ РК и др.;</li>
          <li>Возможность создания новых электронных услуг, таких как электронные аукционы по продаже недвижимости несостоятельных должников и др.</li>
      </ul>
      `,
            },
            kk: {
                title: 'МЕМЛЕКЕТТІК КІРІСТЕР КОМИТЕТІНІҢ АҚПАРАТТЫҚ ЖҮЙЕЛЕРІ',
                content: `<p style="text-align:center"><strong>ИНФОРМАЦИОННЫЕ СИСТЕМЫ КОМИТЕТА ГОСУДАРСТВЕННЫХ ДОХОДОВ</strong></p>
      
      <p style="text-align:center"><strong>МКД АЖ</strong></p>
      
      <p style="text-align:center"><strong><img alt="" src="image://933b7afd-c54b-4e01-96c2-27568cff4f9d" style="float:left; height:150px; width:150px" /></strong></p>
      
      <p>Мемлекеттік кірістер комитетінің ақпараттық жүйелері (бұдан әрі-Жүйе, МКК АЖ) ҚР ҚМ Мемлекеттік кірістер комитетінің кешенді автоматтандырылуына (бизнес-процестерін) арналған және ҚР азаматтары мен бизнесіне электрондық қызметтерді ҚР ҚМ МКК ұсынуын автоматтандыруды қамтиды.</p>
      
      <p>&laquo;Салықтық және кедендік әкімшілендіруді реформалау мақсатында ақпараттық жүйелерді дамыту&raquo; МКК АЖ жобасын іске асыру ҚР ҚМ МКК қолданыстағы және ақпараттық жүйелерін жаңғыртуды және жаңа жүйелерді құруды көздейді.</p>
      
      <p>Жобаның ауқымы негізінен салық салу жүйесіне қосылған салық төлеушілердің жалпы санымен, сондай-ақ салықтық әкімшілендіру саласындағы қызметті жүзеге асыратын ҚР ҚМ МКК ақпараттық жүйелерін пайдаланушылардың санымен айқындалады. Сонымен қатар жобаланатын жүйенің архитектурасын айқындайтын МКК ұйымдық құрылымының иерархия деңгейлерінің саны маңызды болып табылады.</p>
      
      <p style="text-align:center"><strong>Мақсаты</strong></p>
      
      <p>Жоба ҚР ҚМ МКК орындау бойынша әкімшілік рәсімдерді автоматтандыруға арналған және ҚР ҚМ МКК қолданыстағы жаңа ақпараттық жүйелерінің үздіксіз жұмыс істеуіне және дамуына бағытталған.</p>
      
      <p style="text-align:center"><strong>Міндеттер</strong></p>
      
      <ul>
          <li>&laquo;Электрондық үкімет&raquo; құрауыштарымен интеграциялау арқылы МКК АЖ деректерінің растығын қамтамасыз ету</li>
          <li>Халыққа, бизнеске және мемлекеттік органдарға ақпараттық, интерактивті, транзакциялық қызметтерді ұсыну сапасы мен жеделдігіне кепілдік беру</li>
          <li>Барлық ақпараттық құрылымды сүйемелдеуге және әкімшілендіруге жұмсалатын шығындарды азайту</li>
          <li>Елеулі шығындарды талап етпейтін ҚР заңнамасының өзгерістеріне бейімделудің жоғары дәрежесімен икемді ақпараттық жүйелер құру</li>
          <li>Бюджеттік ресурстар мен мемлекеттік қаржыны басқару саласындағы басқару шешімдерінің сапасын және бақылау тиімділігін арттыру;</li>
          <li>Басқару тиімділігін арттыру және өндірістік емес шығындарды қысқарту;</li>
          <li>Халыққа және бизнес-қоғамдастыққа мемлекеттік қызмет көрсетуді ұсыну тиімділігін арттыру</li>
      </ul>
      
      <p style="text-align:center"><strong>МЕМЛЕКЕТ ҮШІН АРТЫҚШЫЛЫҚТАР</strong></p>
      
      <ul>
          <li>Мемлекеттік ведомстволардың өзара іс-қимылының жеделдігі және тиімділігін арттыру<br />
          <em>Бюджетті басқару бойынша барлық ілеспе процестер электрондық форматқа ауыстырылды, орталықтандырылып, ведомствоаралық өзара іс- қимылды неғұрлым тиімді етті.</em></li>
          <li>Сапасы мен сенімділігі</li>
          <li>Ақпаратты автоматты түрде шоғырландыру &laquo;адам факторын&raquo; жоюға мүмкіндік береді</li>
          <li>Үнемдеу<br />
          <em>Есептердің көп санын жинауға және шоғырландыруға жұмсалатын шығындар мен еңбек ресурстарын төмендету.</em></li>
      </ul>
      
      <p style="text-align:center"><strong>ХАЛЫҚ ПЕН БИЗНЕС ҮШІН АРТЫҚШЫЛЫҚТАР</strong></p>
      
      <ul>
          <li>Әкімшілік кедергілерді төмендету және ҚР ҚМ МКК қызмет көрсету жеделдігі мен сапасын арттыру;</li>
          <li>Аудиторларды электрондық аккредитациялау, ҚР ҚМ МКК құзыреті аясында лицензиялар алу және т.б.;</li>
          <li>Дәрменсіз борышкерлердің жылжымайтын мүлкін сату жөніндегі электрондық аукциондар және т.б. сияқты жаңа электрондық қызметтерді құру мүмкіндігі.</li>
      </ul>
      `,
            },
            en: {
                title: 'INFORMATION SYSTEMS OF THE STATE REVENUE COMMITTEE',
                content: `<p style="text-align:center"><strong>INFORMATION SYSTEMS OF THE STATE REVENUE COMMITTEE</strong></p>
      
      <p style="text-align:center"><strong>IS SRC</strong></p>
      
      <p style="text-align:center"><strong><img alt="" src="image://933b7afd-c54b-4e01-96c2-27568cff4f9d" style="float:left; height:150px; width:150px" /></strong></p>
      
      <p>Information systems of the State Revenue Committee (hereinafter referred to as the System, IS SRC) are designed for complex automation (business processes) The State Revenue Committee of the Ministry of Finance of the Republic of Kazakhstan and include automation of the provision of electronic services to citizens and businesses of the Republic of Kazakhstan by the State Revenue Committee of the Ministry of Finance of the Republic of Kazakhstan.</p>
      
      <p>The implementation of the SRC IS project &quot;Development of information systems for the purpose of Reforming Tax and Customs Administration&quot; involves the modernization of existing and information systems of the SRC of the Ministry of Finance of the Republic of Kazakhstan and the creation of new ones.</p>
      
      <p>The scale of the Project is mainly determined by the total number of taxpayers involved in the taxation system, as well as the number of users of the information systems of the SRC of the Ministry of Finance of the Republic of Kazakhstan engaged in activities in the field of tax administration. The number of levels of the hierarchy of the organizational structure of the SRC is also important, which determine, among other things, the architecture of the designed system.</p>
      
      <p style="text-align:center"><strong>Goal</strong></p>
      
      <p>The project is designed to automate administrative procedures for the execution of the SRC of the Ministry of Finance of the Republic of Kazakhstan and is aimed at the smooth operation and development of existing and new information systems of the SRC of the Ministry of Finance of the Republic of Kazakhstan.</p>
      
      <p style="text-align:center"><strong>Tasks</strong></p>
      
      <ol>
          <li>To ensure the reliability of the SRC IS data by integrating with the components of the &quot;electronic government&quot;</li>
          <li>To guarantee the quality and efficiency of the provision of information, interactive, transactional services for the public, business and government agencies</li>
          <li>Reduce the costs of maintenance and administration of the entire information structure</li>
          <li>To create flexible information systems &ndash; with a high degree of adaptation to changes in the legislation of the Republic of Kazakhstan, which does not require significant costs.</li>
          <li>To improve the quality of management decisions and the effectiveness of control in the field of budget resources and public finance management;</li>
          <li>Improve management efficiency and reduce unproductive costs;</li>
          <li>To increase the efficiency of providing public services to the public and the business community<br />
          &nbsp;</li>
      </ol>
      
      <p style="text-align:center"><strong>ADVANTAGES FOR THE STATE</strong></p>
      
      <ol>
          <li>Efficiency and efficiency improvement of interaction between government agencies</li>
          <li>All related budget management processes have been transferred to an electronic format, centralized, which has made interdepartmental interaction more effective.</li>
          <li>Quality and reliability</li>
          <li>Automatic consolidation of information allows you to eliminate the &quot;human factor&quot;;</li>
          <li>Economy</li>
          <li>Reducing costs and labor resources for collecting and consolidating a large number of reports.<br />
          &nbsp;</li>
      </ol>
      
      <p style="text-align:center"><strong>BENEFITS FOR THE PUBLIC AND BUSINESS</strong></p>
      
      <ul>
          <li>Reducing administrative barriers and improving the efficiency and quality of services rendered by the SRC of the Ministry of Finance of the Republic of Kazakhstan;</li>
          <li>Electronic accreditation of auditors, obtaining licenses within the competence of the SRC of the Ministry of Finance of the Republic of Kazakhstan, etc.;</li>
          <li>The possibility of creating new electronic services, such as electronic auctions for the sale of real estate of insolvent debtors, etc.</li>
      </ul>
      `,
            },
        },
        {
            status: {
                current: Statuses.PUBLISH,
            },
            ru: {
                title: 'Документ',
                content: `<p style="text-align:center"><iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen="" frameborder="1" height="315" src="https://www.youtube.com/embed/o1pcstUXg94?si=LKrMPa-IWhuF8BAB" width="560"></iframe></p>
      
      <p>На портале Электронного правительства eGov.kz казахстанцы могут подать на внесения изменения, исправления и дополнения в актовые записи о рождении, заключении и расторжении брака. Новая услуга на портале предусматривает намеренное внесение изменений в документы при наличии достаточных оснований и отсутствии спора между заинтересованными лицами. К примеру, перемена имени, фамилии и отчества.</p>
      
      <p>С помощью новой услуги&nbsp;<a href="https://egov.kz/cms/ru/services/child/pass192_mu" rel="noopener noreferrer" target="_blank">&laquo;Внесение изменений, дополнений и исправлений в записи актов гражданского состояния&raquo;</a>&nbsp;можно подать заявку для внесения изменений в актовых записях и на перевыпуск документов, указав основания и прикрепив подтверждающие документы.</p>
      
      <p><strong>При каких случаях можно внести изменения в cвои документы:&nbsp;</strong>​</p>
      
      <ul>
          <li>При изменении фамилии ребенка на общую фамилию родителей или фамилию одного из родителей;</li>
          <li>при изменении имени, фамилии, исключения отчества несовершеннолетнего ребенка в связи с прекращением брака (супружества) между родителями или признанием брака (супружества) недействительным;</li>
          <li>при изменении фамилии на фамилию, производную от имени отца или деда, по национальным традициям;</li>
          <li>если в записи акта гражданского состояния указаны неправильные сведения;</li>
          <li>при перемене фамилии, имени и отчества.</li>
      </ul>
      
      <p><em>(с полным перечнем можно ознакомиться в&nbsp;<a href="https://adilet.zan.kz/rus/docs/V1500010764" target="_blank">Положении</a></em>&nbsp;<em>(п.167 Правил РАГС от 25 февраля 2015 года и&nbsp;<a href="https://online.zakon.kz/Document/?doc_id=38436792" target="_blank">ст. 257 Кодекса РК &laquo;О браке</a>&nbsp;и семье&raquo;)</em></p>
      
      <p><em>&laquo;Многие</em>&nbsp;<em>казахстанцы не знают, что при желании в актовых записях можно внести изменения самостоятельно</em><em>&nbsp;через</em><em>&nbsp;eGov.kz. Этим самым вы подаете заявку также на перевыпуск свидетельства о рождении, о заключении брака и о расторжении брака с внесенными изменениями в документах. К примеру, если раньше чтобы самому поменять фамилию приходилось идти в ЦОНы, то сейчас это можно сделать электронно и не стоять в очередях&raquo;,-</em><strong>&nbsp;отметил Председатель Правления АО &laquo;Национальные информационные технологии&raquo; Ростислав Коняшкин</strong></p>
      
      <p>&nbsp;</p>
      
      <p>Услугу на портале Электронного правительства можно найти в разделе &laquo;Главная&raquo; - &laquo;Гражданам&raquo; - &laquo;Семья&raquo;.</p>
      
      <p>Чтобы подать заявку для внесения изменений, дополнений и исправлений в записи актов гражданского состояния необходимо:</p>
      
      <ul>
          <li>авторизоваться на портале eGov.kz;</li>
          <li>выбрать необходимый тип услуги;</li>
          <li>выбрать основания для внесения изменений;</li>
          <li>выбрать филиал ЦОНа выдающий свидетельство;</li>
          <li>указать семейное положение и источник дохода;</li>
          <li>указать номер и дату регистрации актовой записи, в которую необходимо внести изменения;</li>
          <li>указать какие изменения, исправления, дополнения необходимы для актовой записи;</li>
          <li>прикрепить подтверждающий документ для внесения изменений;</li>
          <li>произвести оплату;</li>
          <li>подписать запрос.</li>
      </ul>
      
      <p>Результат услуги появится в виде уведомления в статусной странице и Личном кабинете портала eGov.kz. Готовый документ необходимо забрать в ЦОНе, указанный в заявке.</p>
      
      <p>Отметим, данные обновления реализованы в рамках Указа о дебюрократизации Президента РК, важным значением которого является цифровая трансформация и системный реинжиниринг всех сфер государственного управления.</p>
      
      <p>Услуга стала доступна благодаря интеграции информационных систем Министерства юстиции РК, Комитета государственных услуг Министерства цифрового развития, инноваций и аэрокосмической промышленности РК и АО &laquo;Национальные информационные технологии&raquo;.</p>
      `,
            },
            kk: {
                title: 'Құжат',
                content: `<p><img alt="" src="image://f0e92a40-b9ca-4481-a740-6ee602e35793" style="float:right; height:225px; width:415px" /></p>
      
      <p>На портале Электронного правительства eGov.kz казахстанцы могут подать на внесения изменения, исправления и дополнения в актовые записи о рождении, заключении и расторжении брака. Новая услуга на портале предусматривает намеренное внесение изменений в документы при наличии достаточных оснований и отсутствии спора между заинтересованными лицами. К примеру, перемена имени, фамилии и отчества.</p>
      
      <p>С помощью новой услуги&nbsp;<a href="https://egov.kz/cms/ru/services/child/pass192_mu" rel="noopener noreferrer" target="_blank">&laquo;Внесение изменений, дополнений и исправлений в записи актов гражданского состояния&raquo;</a>&nbsp;можно подать заявку для внесения изменений в актовых записях и на перевыпуск документов, указав основания и прикрепив подтверждающие документы.</p>
      
      <p><strong>При каких случаях можно внести изменения в cвои документы:&nbsp;</strong>​</p>
      
      <ul>
          <li>При изменении фамилии ребенка на общую фамилию родителей или фамилию одного из родителей;</li>
          <li>при изменении имени, фамилии, исключения отчества несовершеннолетнего ребенка в связи с прекращением брака (супружества) между родителями или признанием брака (супружества) недействительным;</li>
          <li>при изменении фамилии на фамилию, производную от имени отца или деда, по национальным традициям;</li>
          <li>если в записи акта гражданского состояния указаны неправильные сведения;</li>
          <li>при перемене фамилии, имени и отчества.</li>
      </ul>
      
      <p><em>(с полным перечнем можно ознакомиться в&nbsp;<a href="https://adilet.zan.kz/rus/docs/V1500010764" target="_blank">Положении</a></em>&nbsp;<em>(п.167 Правил РАГС от 25 февраля 2015 года и&nbsp;<a href="https://online.zakon.kz/Document/?doc_id=38436792" target="_blank">ст. 257 Кодекса РК &laquo;О браке</a>&nbsp;и семье&raquo;)</em></p>
      
      <p><em>&laquo;Многие</em>&nbsp;<em>казахстанцы не знают, что при желании в актовых записях можно внести изменения самостоятельно</em><em>&nbsp;через</em><em>&nbsp;eGov.kz. Этим самым вы подаете заявку также на перевыпуск свидетельства о рождении, о заключении брака и о расторжении брака с внесенными изменениями в документах. К примеру, если раньше чтобы самому поменять фамилию приходилось идти в ЦОНы, то сейчас это можно сделать электронно и не стоять в очередях&raquo;,-</em><strong>&nbsp;отметил Председатель Правления АО &laquo;Национальные информационные технологии&raquo; Ростислав Коняшкин</strong></p>
      
      <p>&nbsp;</p>
      
      <p>Услугу на портале Электронного правительства можно найти в разделе &laquo;Главная&raquo; - &laquo;Гражданам&raquo; - &laquo;Семья&raquo;.</p>
      
      <p>Чтобы подать заявку для внесения изменений, дополнений и исправлений в записи актов гражданского состояния необходимо:</p>
      
      <ul>
          <li>авторизоваться на портале eGov.kz;</li>
          <li>выбрать необходимый тип услуги;</li>
          <li>выбрать основания для внесения изменений;</li>
          <li>выбрать филиал ЦОНа выдающий свидетельство;</li>
          <li>указать семейное положение и источник дохода;</li>
          <li>указать номер и дату регистрации актовой записи, в которую необходимо внести изменения;</li>
          <li>указать какие изменения, исправления, дополнения необходимы для актовой записи;</li>
          <li>прикрепить подтверждающий документ для внесения изменений;</li>
          <li>произвести оплату;</li>
          <li>подписать запрос.</li>
      </ul>
      
      <p>Результат услуги появится в виде уведомления в статусной странице и Личном кабинете портала eGov.kz. Готовый документ необходимо забрать в ЦОНе, указанный в заявке.</p>
      
      <p>Отметим, данные обновления реализованы в рамках Указа о дебюрократизации Президента РК, важным значением которого является цифровая трансформация и системный реинжиниринг всех сфер государственного управления.</p>
      
      <p>Услуга стала доступна благодаря интеграции информационных систем Министерства юстиции РК, Комитета государственных услуг Министерства цифрового развития, инноваций и аэрокосмической промышленности РК и АО &laquo;Национальные информационные технологии&raquo;.</p>
      
      <p>&nbsp;</p>
      `,
            },
            en: {
                title: 'Document',
                content: `<p>Changes to documents can be made via eGov.kz portalPrintable version</p>
      
      <p>August 16, 2023</p>
      
      <p>Kazakhstani citizens can apply for making changes, corrections and additions to vital records on birth, marriage and divorce via the eGov.kz portal. The new service enables making changes to documents if there are sufficient grounds and there is no dispute between interested parties. For example, change of surname/name/patronymic.</p>
      
      <p>Using the new service called&nbsp;<a href="https://egov.kz/cms/en/services/child/pass192_mu" target="_blank">&quot;Introduction of changes, additions and corrections to vital records&quot;,</a>&nbsp;you can apply for making changes to the vital records and re-issuance of documents by specifying the grounds and attaching supporting documents.</p>
      
      <p><strong>It is possible to introduce changes to the documents in the following cases:</strong></p>
      
      <ul>
          <li>when changing a child&#39;s surname to a shared surname of parents or surname of one of the parents;</li>
          <li>when changing name, surname, excluding patronymic of a minor child in connection with the divorce or recognition of marriage (matrimony) invalid;</li>
          <li>when changing a surname to a surname derived from a name of a father or grandfather according to national traditions;</li>
          <li>if incorrect data is indicated in a vital record;</li>
          <li>when changing surname, name and patronymic.</li>
      </ul>
      
      <p><em>(The complete list can be found in the Regulatuon (<a href="https://adilet.zan.kz/kaz/docs/V1500010764" target="_blank">Clause 167 of the Civil Registry</a>&nbsp;Office Rules as of February 25, 2015 and Article 257 of the Code of RK&nbsp;<a href="https://adilet.zan.kz/kaz/docs/K1100000518" target="_blank">&quot;On Marriage and Family&quot;</a>)</em></p>
      
      <p><em>&quot;Many Kazakhstani citizens do not know that it is possible to introduce changes to the vital records via eGov.kz. Thus, you also apply for the re-issuance of a birth certificate, marriage/divorce certificate with the changes made in the documents. For instance, if previously one had to visit CSC to change a surname, now it can be done online without the need to wait in queues,&quot;&nbsp;</em><strong>said Rostislav Konyashkin, Chairman of the Board, National Information Technologies JSC.</strong></p>
      
      <p>On the eGov portal, the service can be found in the section &quot;Homepage&quot; - &quot;For Citizenry&quot; - &quot;Family&quot;.</p>
      
      <p>To submit an application for making changes, additions and corrections to vital records, one needs to:</p>
      
      <ul>
          <li>log in on the eGov.kz portal;</li>
          <li>select a relevant type of service;</li>
          <li>select grounds for making changes;</li>
          <li>select the CSC branch for certificate issuance;</li>
          <li>specify marital status and source of income;</li>
          <li>specify the number and registration date for a vital record to be changed;</li>
          <li>specify what changes, additions and corrections are required in a vital record;</li>
          <li>attach a supporting document for introducing changes;</li>
          <li>make a payment;</li>
          <li>sign a request.</li>
      </ul>
      
      <p>The service delivery result will appear in the form of a notification in the status page and personal account of eGov.kz portal. An issued document can be obtained at CSC specified in an application.</p>
      
      <p>It should be noted that the updates were implemented under the framework of the Presidential Decree on the De-Bureaucratization, the importance of which lies in digital transformation and system re-engineering of all the spheres of public administration.</p>
      
      <p>The service was implemented thanks to the integration of information systems of the Ministry of Justice of the Republic of Kazakhstan, State Services Committee of the Ministry of Digital Development, Innovations and Aerospace Industry of the Republic of Kazakhstan and National Information Technologies JSC.</p>
      
      <p>&nbsp;</p>
      
      <p><strong><a href="https://www.youtube.com/watch?v=o1pcstUXg94&amp;ab_channel=SilkWay%2FQazaqstan">CLICK ME</a></strong></p>
      `,
            },
        },
    ].map((a) => ({
        ...a,
        tagId: getRandom(tags).id,
        rating: Math.ceil(getRandomSecure() * 5),
        scores: Math.ceil(getRandomSecure() * 100),
        views: Math.ceil(getRandomSecure() * 100),
    }));

    const articleDTOs = Array(60)
        .fill(null)
        .map(
            (_, i): CreateDTO<Article> => ({
                ...getLangs({
                    title: `Статья ${i + 1}`,
                    content: `Контент статьи ${i + 1}`,
                }),
                status: getRandom(statuses),
                rating: Math.ceil(getRandomSecure() * 5),
                scores: Math.ceil(getRandomSecure() * 100),
                tagId: getRandom(tags).id,
                views: Math.ceil(getRandomSecure() * 100),
            })
        )
        .concat(seedArticles as any);

    const articles = await Promise.all(
        articleDTOs.map((dto) => createArticleApi({ ...dto, parent: getRandom(categories).path }))
    );

    articles.forEach((article) => {
        const linksCount = Math.round(getRandomSecure() * 5);
        const linksSet = new Set<string>();
        for (let i = 0; i < linksCount; i++) {
            linksSet.add(getRandom(articles).id);
        }

        getById<Article>(article.id).then((article) =>
            updateArticleApi({ ...article, linked: [...linksSet] })
        );
    });
};
